import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAudience } from '../context/AudienceContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  ShieldAlert,
  Plus,
  X,
  Loader2,
  AlertTriangle,
  Zap,
  Info,
  Brain,
  Check
} from 'lucide-react';

import { API_URL } from '../config';

export default function AlertsPage() {
  const { token } = useAuth();
  const { audience, isAdult } = useAudience();
  const [medications, setMedications] = useState([]);
  const [currentMed, setCurrentMed] = useState('');
  const [comorbidities, setComorbidities] = useState([]);
  const [currentComorb, setCurrentComorb] = useState('');
  const [renalImpairment, setRenalImpairment] = useState(false);
  const [hepaticImpairment, setHepaticImpairment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);

  const addMedication = () => {
    if (currentMed.trim() && !medications.includes(currentMed.trim())) {
      setMedications([...medications, currentMed.trim()]);
      setCurrentMed('');
    }
  };

  const removeMedication = (med) => {
    setMedications(medications.filter(m => m !== med));
  };

  const addComorbidity = () => {
    if (currentComorb.trim() && !comorbidities.includes(currentComorb.trim())) {
      setComorbidities([...comorbidities, currentComorb.trim()]);
      setCurrentComorb('');
    }
  };

  const removeComorbidity = (comorb) => {
    setComorbidities(comorbidities.filter(c => c !== comorb));
  };

  const checkInteractions = async () => {
    if (medications.length === 0) {
      toast.error('Ajoutez au moins un médicament');
      return;
    }

    setLoading(true);
    setAlerts([]);

    try {
      const response = await fetch(`${API_URL}/api/alerts/check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          medications,
          comorbidities,
          age: isAdult ? 40 : 5,
          renal_impairment: renalImpairment,
          hepatic_impairment: hepaticImpairment
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAlerts(data.alerts || []);
        if (data.alerts?.length === 0) {
          toast.success('Aucune interaction détectée');
        }
      } else {
        toast.error('Erreur lors de la vérification');
      }
    } catch (error) {
      console.error('Error checking interactions:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'grave': return 'text-red-400 bg-red-400/10 border-red-500/30';
      case 'moderee': return 'text-amber-400 bg-amber-400/10 border-amber-500/30';
      case 'info': return 'text-blue-400 bg-blue-400/10 border-blue-500/30';
      default: return 'text-cyan-400 bg-cyan-400/10 border-cyan-500/30';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'grave': return AlertTriangle;
      case 'moderee': return Zap;
      case 'info': return Info;
      default: return ShieldAlert;
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-2xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ShieldAlert className="w-6 h-6 text-cyan-400" />
          Vérification d'interactions
        </h1>
        <p className="text-[hsl(var(--text-dim))]">Détectez les contre-indications et interactions</p>
      </div>

      {/* Medications Input */}
      <div className="glass-card rounded-xl p-6 space-y-4">
        <h2 className="font-semibold">Médicaments</h2>
        <div className="flex gap-2">
          <Input
            value={currentMed}
            onChange={(e) => setCurrentMed(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addMedication()}
            placeholder="Ajouter un médicament..."
            className="flex-1 bg-transparent"
            data-testid="interaction-med-input"
          />
          <Button onClick={addMedication} variant="outline">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {medications.map((med, i) => (
            <Badge key={i} variant="secondary" className="bg-cyan-400/10 text-cyan-300 pr-1" data-testid="interaction-badge">
              {med}
              <button onClick={() => removeMedication(med)} className="ml-2 hover:text-red-400">
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      </div>

      {/* Comorbidities Input */}
      <div className="glass-card rounded-xl p-6 space-y-4">
        <h2 className="font-semibold">Comorbidités (optionnel)</h2>
        <div className="flex gap-2">
          <Input
            value={currentComorb}
            onChange={(e) => setCurrentComorb(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addComorbidity()}
            placeholder="Ajouter une comorbidité..."
            className="flex-1 bg-transparent"
          />
          <Button onClick={addComorbidity} variant="outline">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {comorbidities.map((comorb, i) => (
            <Badge key={i} variant="outline" className="text-[hsl(var(--text-dim))]">
              {comorb}
              <button onClick={() => removeComorbidity(comorb)} className="ml-2 hover:text-red-400">
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      </div>

      {/* Impairments */}
      <div className="glass-card rounded-xl p-6 space-y-4">
        <h2 className="font-semibold">Insuffisances</h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm">Insuffisance rénale</span>
            <Switch 
              checked={renalImpairment} 
              onCheckedChange={setRenalImpairment}
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">Insuffisance hépatique</span>
            <Switch 
              checked={hepaticImpairment} 
              onCheckedChange={setHepaticImpairment}
            />
          </div>
        </div>
      </div>

      {/* Check Button */}
      <Button
        onClick={checkInteractions}
        disabled={loading || medications.length === 0}
        className="w-full bg-cyan-400/90 text-black font-medium hover:bg-cyan-300 glow-cyan"
        data-testid="check-interactions-button"
      >
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            <ShieldAlert className="w-5 h-5 mr-2" />
            Vérifier les interactions
          </>
        )}
      </Button>

      {/* Results */}
      {alerts.length > 0 && (
        <div className="space-y-3 animate-scale-in" data-testid="interactions-panel">
          <h2 className="font-semibold flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            {alerts.length} alerte{alerts.length > 1 ? 's' : ''} détectée{alerts.length > 1 ? 's' : ''}
          </h2>
          
          {alerts.map((alert, i) => {
            const Icon = getSeverityIcon(alert.severity);
            return (
              <div 
                key={i} 
                className={`glass-card rounded-lg p-4 border ${getSeverityColor(alert.severity)}`}
                data-testid="interaction-alert"
              >
                <div className="flex items-start gap-3">
                  <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{alert.medication}</span>
                      <Badge variant="outline" className={`text-xs ${getSeverityColor(alert.severity)}`}>
                        {alert.type === 'contraindication' ? 'Contre-indication' : 
                         alert.type === 'interaction' ? 'Interaction' : 
                         alert.type === 'precaution' ? 'Précaution' : 'Info'}
                      </Badge>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{alert.message}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* No alerts */}
      {alerts.length === 0 && medications.length > 0 && !loading && (
        <div className="glass-card rounded-xl p-6 text-center border-green-500/30">
          <Check className="w-12 h-12 text-green-400 mx-auto mb-3" />
          <h3 className="font-semibold text-green-400">Aucune interaction majeure détectée</h3>
          <p className="text-sm text-[hsl(var(--text-dim))] mt-1">Vérifiez toujours le RCP des médicaments</p>
        </div>
      )}

      {/* Disclaimer */}
      <div className="glass-card rounded-lg p-4 border border-amber-500/30">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-[hsl(var(--text-dim))]">
            Cette vérification ne remplace pas une analyse pharmaceutique complète. 
            Consultez le RCP et un pharmacien pour une analyse exhaustive des interactions.
          </p>
        </div>
      </div>
    </div>
  );
}
